import { z } from 'zod/v4';

import { FILTER_OPERATORS } from 'dashboards/types/filter';
import { SORTS } from 'types/column';
import { STREAM_SOURCES } from 'types/stream';
import { TIME_SERIES_GRANULARITIES } from 'dashboards/types/time';

export const DashboardWidgetSchema = z.object({
  id: z.string(),
  name: z.string(),
  layout: z.object({
    h: z.number().int().min(0),
    w: z.number().int().min(0),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    i: z.string(),
  }),
});

export const FilesWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('files'),
  options: z.object({
    camera_ids: z.array(z.string()).optional(),
    deployment_ids: z.array(z.string()).optional(),
    file_ids: z.array(z.string()).optional(),
    gateway_ids: z.array(z.string()).optional(),
    node_ids: z.array(z.string()).optional(),
    pipeline_ids: z.array(z.string()).optional(),
    stream_ids: z.array(z.string()).optional(),
  }),
});

export const FilesSearchWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('files-search'),
  options: z.object({
    query: z.string(),
    search_entities: z.array(
      z.enum(['events', 'objects', 'texts', 'trackables', 'vectors'])
    ),
    confidence: z.number(),
    sort_by: z.enum(['relevance', 'created_at']),
    camera_ids: z.array(z.string()),
    stream_ids: z.array(z.string()),
    gateway_ids: z.array(z.string()),
    trackable_list_ids: z.array(z.string()).optional(),
    trackable_item_ids: z.array(z.string()).optional(),
  }),
});

const TrackableListSelectionObject = z.object({
  trackable_list_ids: z.array(z.string()).optional(),
  trackable_item_ids: z.array(z.string()).optional(),
});

function requireTrackableListSelection<
  Schema extends z.ZodType<{
    trackable_list_ids?: string[];
  }>
>(schema: Schema) {
  return schema.superRefine((value, ctx) => {
    if (!(value.trackable_list_ids && value.trackable_list_ids.length > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['trackable_list_ids'],
        message: 'At least one trackable list id is required.',
      });
    }
  });
}

export const TrackableStatusWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('trackable-status'),
  options: requireTrackableListSelection(TrackableListSelectionObject),
});

export const StreamWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('stream'),
  streamId: z.string(),
  autoPlay: z.boolean(),
});

export const MultiStreamWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('multistream'),
  options: z.object({
    stream_ids: z.array(z.string()).optional(),
    stream_names: z.array(z.string()).optional(),
    gateway_ids: z.array(z.string()).optional(),
    deployment_ids: z.array(z.string()).optional(),
    sources: z.array(z.enum(STREAM_SOURCES)).optional(),
  }),
  autoPlay: z.boolean(),
});

export const IframeWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('iframe'),
  url: z.string(),
});

const DataWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('data'),
});

export const DataWidgetFiltersInputSchema = z.array(
  z.object({
    id: z.string(),
    column: z.string(),
    operator: z.enum(FILTER_OPERATORS),
    value: z.string().optional(),
    get and() {
      return DataWidgetFiltersInputSchema.optional();
    },
    get or() {
      return DataWidgetFiltersInputSchema.optional();
    },
  })
);

export const DataWidgetTimeSeriesSchema = DataWidgetSchema.extend({
  chartType: z.enum(['timeseries-bar', 'timeseries-line']),
  query: z.object({
    metric: z.string(),
    granularity: z.enum(TIME_SERIES_GRANULARITIES),
    filters: DataWidgetFiltersInputSchema.optional(),
  }),
});

export const DataWidgetTimeSeriesGroupedSchema = DataWidgetSchema.extend({
  chartType: z.enum([
    'timeseries-bar-stacked',
    'timeseries-bar-grouped',
    'timeseries-heatmap',
  ]),
  query: z.object({
    metric: z.string(),
    dimensions: z.array(
      z.object({
        columnName: z.string(),
      })
    ),
    granularity: z.enum(TIME_SERIES_GRANULARITIES),
    filters: DataWidgetFiltersInputSchema.optional(),
  }),
});

export const DataWidgetPieAndLeaderboardSchema = DataWidgetSchema.extend({
  chartType: z.enum(['pie', 'leaderboard-bar', 'leaderboard-table']),
  query: z.object({
    metric: z.string(),
    dimensions: z.array(
      z.object({
        columnName: z.string(),
      })
    ),
    rowLimit: z.number(),
    sort: z.enum(SORTS),
    filters: DataWidgetFiltersInputSchema.optional(),
  }),
});

export const DataWidgetTableSchema = DataWidgetSchema.extend({
  chartType: z.literal('table'),
  query: z.object({
    dataPool: z.object({ name: z.string() }),
    columns: z.array(z.string()),
    orderByColumn: z.string(),
    sort: z.enum(SORTS),
    filters: DataWidgetFiltersInputSchema.optional(),
  }),
});

export const DataWidgetCounterSchema = DataWidgetSchema.extend({
  chartType: z.literal('counter'),
  query: z.object({
    metric: z.string(),
    filters: DataWidgetFiltersInputSchema.optional(),
  }),
});

export const MarkdownWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('markdown'),
  content: z.string(),
});

export const CamerasWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('cameras'),
  options: z.object({
    camera_ids: z.array(z.string()).optional(),
    deployment_ids: z.array(z.string()).optional(),
    gateway_ids: z.array(z.string()).optional(),
    sources: z.array(z.string()).optional(),
  }),
  autoPlay: z.boolean(),
});

export const SystemStateWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('system-state'),
});

export const SystemEventsWidgetSchema = DashboardWidgetSchema.extend({
  type: z.literal('system-events'),
  limit: z.number().optional(),
  options: z.object({
    camera_ids: z.array(z.string()).optional(),
    gateway_ids: z.array(z.string()).optional(),
    stream_ids: z.array(z.string()).optional(),
    deployment_ids: z.array(z.string()).optional(),
    event_types: z.array(z.string()).optional(),
    severities: z.array(z.enum(['error', 'warning', 'info', 'debug'])).optional(),
  }).optional(),
});

export const DashboardConfigurationSchema = z.object({
  widgets: z.array(
    z.union([
      FilesWidgetSchema,
      FilesSearchWidgetSchema,
      TrackableStatusWidgetSchema,
      StreamWidgetSchema,
      MultiStreamWidgetSchema,
      IframeWidgetSchema,
      DataWidgetTimeSeriesSchema,
      DataWidgetTimeSeriesGroupedSchema,
      DataWidgetPieAndLeaderboardSchema,
      DataWidgetTableSchema,
      DataWidgetCounterSchema,
      MarkdownWidgetSchema,
      CamerasWidgetSchema,
      SystemStateWidgetSchema,
      SystemEventsWidgetSchema,
    ])
  ),
});
